import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, X, ArrowLeft, CheckCircle } from 'lucide-react';

const PASSWORD_RULES = [
  { test: (p) => p.length >= 12, label: 'Минимум 12 символов' },
  { test: (p) => /[A-ZА-ЯЁ]/.test(p), label: 'Заглавная буква' },
  { test: (p) => /[a-zа-яё]/.test(p), label: 'Строчная буква' },
  { test: (p) => /[0-9]/.test(p), label: 'Цифра' },
  { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(p), label: 'Спецсимвол (!@#$%^&*...)' },
];

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  return (
    <div className="space-y-1 mt-2">
      {PASSWORD_RULES.map((rule, i) => {
        const passed = rule.test(password);
        return (
          <div key={i} className={`flex items-center gap-1.5 text-xs ${passed ? 'text-green-600' : 'text-gray-400'}`}>
            {passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
            {rule.label}
          </div>
        );
      })}
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Если нет токена в URL - показываем ошибку
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-red-600">Ошибка</CardTitle>
            <CardDescription className="text-center">
              Ссылка для сброса пароля недействительна или отсутствует.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password">
              <Button className="w-full">
                Запросить новую ссылку
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    const allPassed = PASSWORD_RULES.every((r) => r.test(newPassword));
    if (!allPassed) {
      setError('Пароль не соответствует требованиям безопасности');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPasswordConfirm(token, newPassword);
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Ссылка недействительна или истекла. Запросите новую ссылку.');
      } else {
        setError(err.response?.data?.detail || 'Ошибка сброса пароля. Попробуйте позже.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Пароль изменён</CardTitle>
            <CardDescription className="text-center">
              Ваш пароль успешно изменён. Теперь вы можете войти с новым паролем.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Войти в систему
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Новый пароль</CardTitle>
          <CardDescription className="text-center">
            Придумайте новый надёжный пароль
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="newPassword">Новый пароль</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
              />
              <PasswordStrength password={newPassword} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Пароли не совпадают</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !PASSWORD_RULES.every((r) => r.test(newPassword)) || newPassword !== confirmPassword}
            >
              {loading ? 'Сохранение...' : 'Установить пароль'}
            </Button>

            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Вернуться на страницу входа
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
